import { ComponentPortal, PortalModule, TemplatePortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatSidenavModule } from '@angular/material/sidenav';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { FilePanelService } from '@lib/components/document-viewer/file-viewer/file-panel.service';
import { FileViewerComponent } from '@lib/components/document-viewer/file-viewer/file-viewer.component';
import { MigrationErrorPageComponent } from '@lib/components/error-pages/migration-error-page/migration-error-page.component';
import { TechnicalErrorPageComponent } from '@lib/components/error-pages/technical-error-page/technical-error-page.component';
import { FiltrerInvestisseursComponent } from '@lib/components/filtrer-investisseurs/filtrer-investisseurs.component';
import { QPIViewerPageComponent } from '@lib/components/investisseur-details/tabs/connaissance-client/steps/qpi-step/qpi-viewer-page/qpi-viewer-page.component';
import { SignataireDetailsComponent } from '@lib/components/signature/signataire-details/signataire-details.component';
import { SignaturePanelService } from '@lib/components/signature/signature-panel.service';
import { AuthService } from '@lib/services/auth-service.service';
import { ChunkErrorGuardService } from '@lib/services/chunk-error-guard.service';
import { DocumentViewerPanelService } from '@lib/services/document-viewer-panel.service';
import { FiltrerInvestisseursPanelService } from '@lib/services/filtrer-investisseurs/filtrer-investisseurs-panel.service';
import { LayoutService } from '@lib/services/layout.service';
import { LoadingService } from '@lib/services/loading.service';
import { QpiViewerPanelService } from '@lib/services/qpi-viewer-panel.service';
import { DerViewerPanelService } from '@lib/services/der-viewer-panel.service';
import { DERViewerPageComponent } from '@lib/components/investisseur-details/tabs/connaissance-client/steps/der-step/der-viewer-page/der-viewer-page.component';
import { SwUpdateService } from '@lib/services/sw-update.service';
import { OuiErrorPageService } from 'omnium-ui/error-page';
import { OuiSlidePanelService } from 'omnium-ui/layout';
import { MenuNode } from 'omnium-ui/shared';
import { SHARED_IMPORTS } from 'projects/minotaure-lib/src/shared-imports';
import { filter, tap } from 'rxjs';
import { RecueilDocumentViewerComponent } from './components/document-viewer/recueil-document-viewer/recueil-document-viewer.component';
import { ApplicationSettingsService } from './services/application-settings.service';
import { ConsultantPreferencesService } from '@lib/services/consultant-preferences.service';
import { DocumentRequestPanelService } from '@lib/components/investisseur-details/tabs/documents/document-request-creation/document-request-panel.service';
import { DocumentRequestCreationComponent } from '@lib/components/investisseur-details/tabs/documents/document-request-creation/document-request-creation.component';
import { derStepComponent } from '@lib/components/investisseur-details/tabs/connaissance-client/steps/der-step/der-step.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SHARED_IMPORTS,
    RouterModule,
    RouterOutlet,
    RecueilDocumentViewerComponent,
    MatSidenavModule,
    PortalModule,
    FiltrerInvestisseursComponent,
    QPIViewerPageComponent,
    DERViewerPageComponent,
    SignataireDetailsComponent,
    FileViewerComponent,
    DocumentRequestCreationComponent,
    derStepComponent,
  ],
})
export class AppComponent implements OnInit, AfterViewInit {
  public readonly layoutService = inject(LayoutService);
  public readonly authService = inject(AuthService);
  private readonly preferencesService = inject(ConsultantPreferencesService);
  private readonly router = inject(Router);

  private readonly updateService = inject(SwUpdateService); // juste pour instancier le service et lancer la logique de vérification des mises à jour
  private readonly chunkGardService = inject(ChunkErrorGuardService); // juste pour instancier le service et lancer la logique de surveillance des erreurs de chargement de composants

  private readonly documentViewerService = inject(DocumentViewerPanelService);
  private readonly qpiViewerPanelService = inject(QpiViewerPanelService);
  private readonly derViewerPanelService = inject(DerViewerPanelService);
  private readonly filterInvestisseursPanelService = inject(FiltrerInvestisseursPanelService);
  private readonly signaturePanelService = inject(SignaturePanelService);
  private readonly filePanelService = inject(FilePanelService);
  protected readonly documentRequestPanelService = inject(DocumentRequestPanelService);
  private readonly slidePanelService = inject(OuiSlidePanelService);
  private readonly _viewContainerRef = inject(ViewContainerRef);
  private readonly injector = inject(Injector);
  private readonly errorPageService = inject(OuiErrorPageService);
  protected readonly loadingService = inject(LoadingService);
  private readonly appSettingsService = inject(ApplicationSettingsService);
  protected readonly environment = inject<any>('environment' as any);

  readonly logoSrc = 'assets/LogoProxeema.svg';
  readonly navLogoSrc = 'assets/LogoAppProxeema_nav.svg';
  readonly helpHref = 'https://prodemial.zendesk.com';
  readonly baseMenuItems: MenuNode[] = [
    {
      name: 'Accueil',
      routeLink: '/dashboard',
      icon: 'home',
    },
    {
      name: 'Investisseurs',
      routeLink: '/investisseurs',
      icon: 'groups',
    },
    {
      name: 'Contrats Stellium',
      routeLink: '/contrats-stellium',
      icon: 'chart_data',
    },
    {
      name: 'Suivi documents',
      routeLink: '/suivi-documents',
      icon: 'docs',
    },
    {
      name: 'Suivi opérations',
      routeLink: '/suivi-operations',
      icon: 'folder_open',
    },
    {
      name: 'Suivi signatures',
      routeLink: '/signatures',
      icon: 'signature',
    },
  ];

  readonly avatarMenuItems: MenuNode[] = [
    {
      icon: 'settings',
      name: 'Préférences',
      action: () => this.openPreferencesSettings(),
    },
    {
      name: 'Notes de version',
      action: () => this.openVersionNotes(),
      icon: 'open_in_new',
    },
    {
      name: 'Se déconnecter',
      action: () => this.authService.logout(),
      icon: 'power_settings_new',
    },
  ];

  readonly secondaryMenuItems: MenuNode[] = [
    {
      name: 'Mentions légales',
      action: () => this.openMentionsLegales(),
    },
    {
      name: "Conditions générales d'utilisation",
      action: () => this.openCgu(),
    },
  ];

  readonly computedMenuItems = computed(() => {
    const displayMyStelliumEvol = environment.displayMyStelliumEvol;
    const displayOperations = this.authService.hasFeatureTag('activateOperationFeatures');

    return this.baseMenuItems.filter(
      item => (displayMyStelliumEvol || item.name !== 'Suivi documents') && (displayOperations || item.name !== 'Suivi opérations')
    );
  });

  @ViewChild('documentViewerPortalContent') documentViewerPortalContent: TemplateRef<unknown>;
  @ViewChild('qpiViewerPortalContent') qpiViewerPortalContent: TemplateRef<unknown>;
  @ViewChild('derViewerPortalContent') derViewerPortalContent: TemplateRef<unknown>;
  @ViewChild('filtrerPortalContent') filtrerPortalContent: TemplateRef<unknown>;
  @ViewChild('signatairePortalContent') signatairePortalContent: TemplateRef<unknown>;
  @ViewChild('filePortalContent') filePortalContent: TemplateRef<unknown>;
  @ViewChild('reglementPortalContent') reglementPortalContent: TemplateRef<unknown>;
  @ViewChild('documentRequestPortalContent') documentRequestPortalContent: TemplateRef<unknown>;
  documentViewerPortal: TemplatePortal;
  qpiViewerPortal: TemplatePortal;
  derViewerPortal: TemplatePortal;
  filterPortal: TemplatePortal;
  signatairePortal: TemplatePortal;
  filePortal: TemplatePortal;
  reglementPortal: TemplatePortal;
  documentRequestPortal: TemplatePortal;
  slidePanelContent: any;

  readonly searchControl = new FormControl<string>({ value: '', disabled: true });

  private readonly _canAccessAppEffect = effect(() => {
    const canAccessError = this.authService.accessCheckError();
    const canAccess = this.authService.canAccessApp();

    if (canAccessError) {
      const portal = new ComponentPortal(TechnicalErrorPageComponent, null, this.injector);
      this.errorPageService.displayErrorPage('Oups ! Quelque chose ne va pas.', 'error', portal);
    } else if (!canAccess) {
      const portal = new ComponentPortal(MigrationErrorPageComponent, null, this.injector);
      this.errorPageService.displayErrorPage('Presque prêt !', 'migration', portal);
    } else {
      this.errorPageService.hideErrorPage();
    }
  });

  currentVersion = this.environment.appVersion;

  readonly cguFile: string = '../assets/CGU.pdf';
  readonly mentionsLegalesFile: string = '../assets/Mentions.pdf';
  reglementFile = signal<string>('');
  reglementOpened = signal<boolean>(false);

  readonly destroyRef = inject(DestroyRef);

  constructor() {}
  ngOnInit(): void {
    this.initApp();
  }

  ngAfterViewInit(): void {
    this.documentViewerPortal = new TemplatePortal(this.documentViewerPortalContent, this._viewContainerRef);
    this.documentViewerService.registerPortal(this.documentViewerPortal);
    this.documentViewerService.viewerClosed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.qpiViewerPortal = new TemplatePortal(this.qpiViewerPortalContent, this._viewContainerRef);
    this.qpiViewerPanelService.registerPortal(this.qpiViewerPortal);
    this.qpiViewerPanelService.viewerClosed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.derViewerPortal = new TemplatePortal(this.derViewerPortalContent, this._viewContainerRef);
    this.derViewerPanelService.registerPortal(this.derViewerPortal);
    this.derViewerPanelService.viewerClosed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.filterPortal = new TemplatePortal(this.filtrerPortalContent, this._viewContainerRef);
    this.filterInvestisseursPanelService.registerPortal(this.filterPortal);
    this.filterInvestisseursPanelService.viewerClosed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.signatairePortal = new TemplatePortal(this.signatairePortalContent, this._viewContainerRef);
    this.signaturePanelService.registerPortal(this.signatairePortal);
    this.signaturePanelService.viewerClosed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.reglementPortal = new TemplatePortal(this.reglementPortalContent, this._viewContainerRef);

    this.documentRequestPortal = new TemplatePortal(this.documentRequestPortalContent, this._viewContainerRef);
    this.documentRequestPanelService.registerPortal(this.documentRequestPortal);
    this.documentRequestPanelService.viewerClosed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.filePortal = new TemplatePortal(this.filePortalContent, this._viewContainerRef);
    this.filePanelService.registerPortal(this.filePortal);
    this.filePanelService.viewerClosed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private async initApp() {
    try {
      await this.authService.login();
    } catch (error) {
      console.log('Login failed with error: ', error);
      return;
    }

    this.slidePanelService.slidePanelContent$.subscribe(value => {
      this.slidePanelContent = value;
    });

    this.slidePanelService.slidePanelState$
      .pipe(
        filter(state => state === 'closed' && this.reglementOpened()),
        tap(() => this.onReglementPanelClose()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.appSettingsService.initApplicationSettings();
  }

  private openVersionNotes() {
    window.open(this.environment.notesVersions, '_blank');
  }

  private openMentionsLegales() {
    this.openReglementDocumentSlidePanel(this.mentionsLegalesFile);
  }

  private openCgu() {
    this.openReglementDocumentSlidePanel(this.cguFile);
  }

  private openReglementDocumentSlidePanel(fileUrl: string): void {
    this.reglementFile.set(fileUrl);
    this.reglementOpened.set(true);
    this.slidePanelService.setSlidePanelContent(this.reglementPortal);
    this.slidePanelService.openSlidePanel({ position: 'right', autoFocus: false, disableClose: false });
  }

  private onReglementPanelClose() {
    this.reglementOpened.set(false);
    this.reglementFile.set('');
  }

  private openPreferencesSettings() {
    void this.router.navigate(['/preferences']);
  }
}
